/**
 * Typed adapter contracts for the Milestone E onboarding workspace.
 *
 * Backend is the ONLY source of truth. Adapters coerce unknown values
 * (classification/status/overall_status/reason) to safe defaults so a
 * schema drift never crashes the workspace — unknown becomes a
 * read-only presentation.
 */

export const READINESS_ITEM_KEYS = [
  "tenant_profile_confirmed",
  "company_configured",
  "branch_configured",
  "owner_admin_exists",
  "manager_trainer_exists",
  "representative_exists",
  "template_selected",
  "pipeline_stages_ready",
  "contact_import_ready",
  "training_material_ready",
] as const;
export type ReadinessItemKey = (typeof READINESS_ITEM_KEYS)[number];

export const PANEL_KEYS = [
  "tenant-profile",
  "organization",
  "users-roles",
  "template",
  "pipeline",
  "contact-import",
  "launch-readiness",
] as const;
export type PanelKey = (typeof PANEL_KEYS)[number];

export type Classification = "acknowledged" | "automatic" | "unknown";
export type ItemStatus = "complete" | "incomplete" | "unknown";
export type OverallStatus =
  | "not_started"
  | "in_progress"
  | "ready_to_complete"
  | "completed"
  | "unknown";

export interface OnboardingItem {
  key: ReadinessItemKey | string;
  classification: Classification;
  status: ItemStatus;
  reason: string | null;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  note?: string | null;
  prerequisite_ready?: boolean;
}

export interface OnboardingState {
  tenant_id: string;
  overall_status: OverallStatus;
  completion_pct: number;
  items: OnboardingItem[];
  incomplete_keys: string[];
  started_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  last_updated_at: string | null;
  updated_by: string | null;
  selected_template_key: string | null;
  selected_template_version: string | null;
  version: number;
}

export interface TenantTemplateSummary {
  key: string;
  display_name: string;
  description: string;
  target_business_type: string;
  template_version: string;
  active: boolean;
  stages: Array<{
    canonical_stage: string;
    order: number;
    label: string;
    guidance?: string;
  }>;
}

/** Coerce any unknown backend payload into a fully-typed
 * ``OnboardingState`` — never throws, always returns a safe read-only
 * shape. */
export function toOnboardingState(raw: unknown): OnboardingState {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rawItems = Array.isArray(r.items) ? (r.items as unknown[]) : [];
  const items: OnboardingItem[] = rawItems.map(toOnboardingItem);
  const known: OverallStatus[] = [
    "not_started",
    "in_progress",
    "ready_to_complete",
    "completed",
  ];
  const overall = (typeof r.overall_status === "string" &&
    (known as string[]).includes(r.overall_status))
    ? (r.overall_status as OverallStatus)
    : "unknown";
  const pct = typeof r.completion_pct === "number" && Number.isFinite(r.completion_pct)
    ? Math.max(0, Math.min(100, Math.round(r.completion_pct)))
    : 0;
  return {
    tenant_id: typeof r.tenant_id === "string" ? r.tenant_id : "",
    overall_status: overall,
    completion_pct: pct,
    items,
    incomplete_keys: Array.isArray(r.incomplete_keys)
      ? (r.incomplete_keys as string[]).filter((k) => typeof k === "string")
      : items.filter((i) => i.status !== "complete").map((i) => i.key),
    started_at: (r.started_at as string | null) ?? null,
    completed_at: (r.completed_at as string | null) ?? null,
    completed_by: (r.completed_by as string | null) ?? null,
    last_updated_at: (r.last_updated_at as string | null) ?? null,
    updated_by: (r.updated_by as string | null) ?? null,
    selected_template_key: (r.selected_template_key as string | null) ?? null,
    selected_template_version: (r.selected_template_version as string | null) ?? null,
    version: typeof r.version === "number" ? r.version : 1,
  };
}

export function toOnboardingItem(raw: unknown): OnboardingItem {
  const r = (raw ?? {}) as Record<string, unknown>;
  const classification: Classification =
    r.classification === "acknowledged" || r.classification === "automatic"
      ? r.classification
      : "unknown";
  const status: ItemStatus =
    r.status === "complete" || r.status === "incomplete" ? r.status : "unknown";
  return {
    key: typeof r.key === "string" ? r.key : "unknown",
    classification,
    status,
    reason: (r.reason as string | null) ?? null,
    acknowledged_at: (r.acknowledged_at as string | null) ?? null,
    acknowledged_by: (r.acknowledged_by as string | null) ?? null,
    note: (r.note as string | null) ?? null,
    prerequisite_ready: typeof r.prerequisite_ready === "boolean"
      ? r.prerequisite_ready
      : undefined,
  };
}

export function toTemplateSummary(raw: unknown): TenantTemplateSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.key !== "string") return null;
  const stages = Array.isArray(r.stages) ? (r.stages as unknown[]) : [];
  return {
    key: r.key,
    display_name:
      typeof r.display_name === "string" ? r.display_name : r.key,
    description: typeof r.description === "string" ? r.description : "",
    target_business_type:
      typeof r.target_business_type === "string" ? r.target_business_type : "",
    template_version:
      typeof r.template_version === "string" ? r.template_version : "",
    active: r.active !== false,
    stages: stages
      .map((s) => s as Record<string, unknown>)
      .filter((s) => typeof s.canonical_stage === "string")
      .map((s) => ({
        canonical_stage: s.canonical_stage as string,
        order: typeof s.order === "number" ? s.order : 0,
        label: typeof s.label === "string" ? s.label : (s.canonical_stage as string),
        guidance: typeof s.guidance === "string" ? s.guidance : "",
      }))
      .sort((a, b) => a.order - b.order),
  };
}

/** Map an item key to its owning panel. Unknown keys map to null so
 * they render inline without opening a panel. */
export function panelForItem(key: string): PanelKey | null {
  switch (key) {
    case "tenant_profile_confirmed":
      return "tenant-profile";
    case "company_configured":
    case "branch_configured":
      return "organization";
    case "owner_admin_exists":
    case "manager_trainer_exists":
    case "representative_exists":
      return "users-roles";
    case "template_selected":
      return "template";
    case "pipeline_stages_ready":
      return "pipeline";
    case "contact_import_ready":
      return "contact-import";
    case "training_material_ready":
      return "launch-readiness";
    default:
      return null;
  }
}

export function panelTitle(p: PanelKey): string {
  switch (p) {
    case "tenant-profile":
      return "Tenant Profile";
    case "organization":
      return "Organization Structure";
    case "users-roles":
      return "Users and Roles";
    case "template":
      return "CRM Template";
    case "pipeline":
      return "Pipeline";
    case "contact-import":
      return "Contact Import";
    case "launch-readiness":
      return "Launch Readiness";
  }
}

export function isValidPanelKey(v: unknown): v is PanelKey {
  return typeof v === "string" && (PANEL_KEYS as readonly string[]).includes(v);
}
